import { Injectable } from '@angular/core';
import { UserAgentApplication } from 'msal';
import { environment } from '../../../environments/environment';
import { BroadcastService } from './broadcast.service';
import {
  MSAL_ERROR_MESSAGE_BLOCK_TOKEN_REQUESTS,
  MSAL_ERROR_MESSAGE_INTERACTION_REQUIRED,
  MSAL_ERROR_MESSAGE_USER_LOGIN_ERROR
} from '../constants';
import {throwError} from 'rxjs';

// const baseAuthURL = 'https://login.microsoftonline.com/tfp/';
const baseAuthURL = 'https://emlportal.b2clogin.com/';

// The password reset relying parties do not output trustFrameworkPolicy, so their tokens carry no
// tfp claim and the reset flow cannot be identified from the response. Record that we sent the user
// there instead. loginRedirect() reloads the page, so this has to survive navigation.
const PASSWORD_RESET_PENDING_KEY = 'eml.cmp.passwordResetPending';

const msalConfig = {
  auth: {
    clientId: environment.aad_app_id,
    authority: `${baseAuthURL}${environment.aad_tennant_id}/${environment.aad_signin_flow}`,
    validateAuthority: false
  },
  // cache: {
  //   storeAuthStateInCookie: true
  // },
  // framework: {
  //   isAngular: true
  // }
};

@Injectable()
export class MsalService extends UserAgentApplication {

  loginRequest = {
    scopes: [environment.aad_app_id]
  };

  processingRedirect = false;
  hasProcessedRedirect = false;

  constructor(private broadcastService: BroadcastService) {
    super(msalConfig);
    this.processingRedirect = false;
    this.hasProcessedRedirect = false;
    this.handleRedirectCallback(this.processRedirectCallback);
  }

  checkAuth = (): boolean => {
    return (this.getAccount() !== null && this.checkCacheforToken() !== null);
  }

  login = (): void => {
    // NOTE: A successful login or signup redirect will map to what ever is set to the 'handleRedirectCallback' on the msalInstance
    // This will automatically fire thanks to the MSAL library :)
    this.setAuthorityToSignIn();
    this.loginRedirect();
  }

  signup = (extraQueryParameters = {}): void => {
    // The should push a user to the SIGNIN flow
    this.setAuthorityToSignUp();
    this.loginRedirect({extraQueryParameters});
  }

  // Our custom handler for processing the redirect callback from Azure Active Directory B2C
  processRedirectCallback = (error, response) => {
    this.processingRedirect = true;
    this.hasProcessedRedirect = true;

    if (error) {
      // This here gives us an opportunity to intereact with any specific error messages are
      // are waiting from from teh Azure B2C platform.
      console.log('ERROR: Failure to correctly log user in on Azure Directory B2C Panel.');
      console.log(error);

      // A reset that errored or was cancelled must not leave the marker behind, or the next
      // successful sign in would be taken for a post-reset one and bounced straight back out.
      // passwordReset() sets it again if that is the branch we take below.
      this.clearPasswordResetPending();

      // This is the error code that is sent back using the password redirect

      /* FORGOT PASSWORD ERROR RESPONSE */
      /* This will be fired if a user clicks the forgot password link on the sign IN page */
      if (error.message.includes('AADB2C90118')) {
        this.passwordReset();
        /* SIGNUP ERROR RESPONSE */
        /* This will be fired if a user hits cancel on the sign UP page */
      } else if (error.message.includes('AADB2C90091')) {
        this.login();
        /* DEFAULT ERROR RESPONSE */
        /* All other redirect errors will fall back to pushing a user back to the login screen again */
      } else {
        this.login();
      }

      return;
    }

    // Due to having multiple flows, the authority gathered when processing a redirect needs to be changed here
    // to match what ever is on the token. We default this to the SIGN_IN flow, however it may not
    // be what we have authenticated the user with.
    const tokenAuthority = response.idTokenClaims.tfp;

    /* PASSWORD RESET COMPLETED */
    /* The reset flow returns a token of its own, which would drop the user straight into the portal
       without ever running the sign in journey. Sign them out instead so they come back through sign
       in with their new password - that journey is where email verification happens. */
    if (this.consumePasswordResetPending()) {
      this.processingRedirect = false;
      this.setAuthorityToSignIn();
      this.logout();
      return;
    }

    this.setAuthority(tokenAuthority);

    // Fire a login succes message!
    this.broadcastService.broadcast('msal:loginSuccess', response);

    // TODO: Expand this out to include other functions (such as different types of B2C Requests, reset password etc)
    // Ok lets start processing the response
    // this.acquireTokenSilentInternal();
    this.processingRedirect = false;
  }

  isProcessingRedirect = () => {
    return this.processingRedirect;
  }

  didProcessedRedirect = () => {
    return this.hasProcessedRedirect;
  }

  passwordReset = () => {
    sessionStorage.setItem(PASSWORD_RESET_PENDING_KEY, 'true');
    this.setAuthority(environment.aad_forget_password_flow);
    this.loginRedirect();
  }

  // True only for the redirect returning from a reset we started ourselves. Reads once and clears,
  // so a later sign in is never mistaken for a post-reset one.
  consumePasswordResetPending = (): boolean => {
    const pending = sessionStorage.getItem(PASSWORD_RESET_PENDING_KEY) === 'true';
    if (pending) {
      this.clearPasswordResetPending();
    }
    return pending;
  }

  clearPasswordResetPending = (): void => {
    sessionStorage.removeItem(PASSWORD_RESET_PENDING_KEY);
  }

  setAuthorityToSignUp = () => {
    this.setAuthority(environment.aad_signup_flow);
  }

  setAuthorityToSignIn = () => {
    this.setAuthority(environment.aad_signin_flow);
  }

  setAuthority = (userFlow: string) => {
    this.authority = `${baseAuthURL}${environment.aad_tennant_id}/${userFlow}`;
  }

  checkCacheforToken = (): any => {
    const account = this.getAccount();
    if (account) {
      if (account.idToken && account.idToken.tfp) {
        const tokenAuthority = account.idToken.tfp;
        this.setAuthority(tokenAuthority);
      }
      const internalToken = this.getCachedTokenInternal(this.loginRequest.scopes, account, null);
      return internalToken;
    } else {
      return null;
    }
  }

  updateToken = (): Promise<boolean> => {
    return this.acquireTokenSilent(this.loginRequest)
      .then(() => {
        return true
      })
      .catch((error) => {
        console.log(`Token update failed. Error: ${error}.`)
        throw error
      })
  }

  _logout() {
    this.logout();
  }

}
